<template>
  <div class="main-wrapper" v-if="extensions">
    <el-tabs v-model="state" @tab-click="btnNavClick">
      <el-tab-pane :label="'全部插件(' + allCount + ')'" name="All"></el-tab-pane>
      <el-tab-pane :label="'待审核插件(' + pendingCount + ')'" name="Pending"></el-tab-pane>
      <el-tab-pane :label="'已发布插件(' + publishedCount + ')'" name="Published"></el-tab-pane>
      <el-tab-pane :label="'已拒绝插件(' + rejectedCount + ')'" name="Rejected"></el-tab-pane>
    </el-tabs>

    <el-table :data="extensions">
      <el-table-column align="center" width="80">
        <template #default="scope">
          <img :src="getIconUrl(scope.row)" width="48" height="48" />
        </template>
      </el-table-column>
      <el-table-column label="作者" width="100">
        <template #default="scope">
          {{ scope.row.userName }}
        </template>
      </el-table-column>
      <el-table-column label="名称" width="140">
        <template #default="scope">
          {{ scope.row.name }}
        </template>
      </el-table-column>
      <el-table-column label="显示名称" width="140">
        <template #default="scope">
          {{ scope.row.displayName }}
        </template>
      </el-table-column>
      <el-table-column label="版本号" width="80">
        <template #default="scope">
          {{ scope.row.latestVersion }}
        </template>
      </el-table-column>
      <el-table-column label="更新时间" width="140">
        <template #default="scope">
          {{ scope.row.updatedDate }}
        </template>
      </el-table-column>
      <el-table-column label="标签">
        <template #default="scope">
          <el-link v-for="keyword in scope.row.keywords" :key="keyword" type="success" :underline="false" style="margin-right: 10px" @click.prevent="btnTagsClick(scope.row)">
            {{ keyword }}
          </el-link>
        </template>
      </el-table-column>
      <el-table-column label="状态" width="200">
        <template #default="scope">
          <div v-if="scope.row.state === 'Rejected'">
            <small style="color: #f56c6c">
              审核未通过：
              {{ scope.row.message }}
            </small>
          </div>
          <div v-else>
            <el-link :type="scope.row.state == 'Published' ? 'success' : 'danger'" :underline="false">
              {{ getResourceState(scope.row) }}
            </el-link>
          </div>
        </template>
      </el-table-column>
      <el-table-column label="价格" width="100">
        <template #default="scope">
          <el-tag v-if="scope.row.price == 0" type="primary">免 费</el-tag>
          <el-tag v-else type="danger">￥{{ scope.row.price }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="操作">
        <template #default="scope">
          <div class="actions">
            <el-link type="primary" @click.prevent="btnViewClick(scope.row)">查看</el-link>
            <el-link type="primary" :href="getDownloadUrl(scope.row)">下载</el-link>
            <el-link type="primary" @click.prevent="btnStateClick(scope.row)">设置</el-link>
            <el-link type="danger" @click.prevent="btnDeleteClick(scope.row)">删除</el-link>
          </div>
        </template>
      </el-table-column>
    </el-table>

    <el-drawer
      :title="extension.userName + '.' + extension.name"
      direction="rtl"
      size="80%"
      v-if="extension"
      v-model="isTableView"
    >
      <table-view :extension="extension" :releases="releases"></table-view>
    </el-drawer>

    <el-dialog title="设置插件状态" v-model="isFormState" width="40%">
      <form-state :extension="extension" @cancel="isFormState = false" @submit="updateExtension"></form-state>
    </el-dialog>

    <el-dialog title="设置插件标签" v-model="isFormTags" width="40%">
      <form-tags :extension="extension" @cancel="isFormTags = false" @submit="updateExtension"></form-tags>
    </el-dialog>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { IExtension, IExtensionRelease } from '@/types/models';
import utils from '@/utils';
import api from '@/api/admin/extensions';
import TableView from './components/extensions/TableView.vue';
import FormState from './components/extensions/FormState.vue';
import FormTags from './components/extensions/FormTags.vue';

interface IState {
  state: string;
  extensions: IExtension[];
  allTagNames: string[];
  allCount: number;
  pendingCount: number;
  publishedCount: number;
  rejectedCount: number;
  isTableView: boolean;
  extension: IExtension;
  releases: IExtensionRelease[];
  isFormState: boolean;
  isFormTags: boolean;
}

export default defineComponent({
  components: { TableView, FormState, FormTags },
  data() {
    return {
      state: 'All',
      extensions: [],
      allTagNames: [],
      allCount: 0,
      pendingCount: 0,
      publishedCount: 0,
      rejectedCount: 0,
      isTableView: false,
      extension: {} as IExtension,
      releases: [],
      isFormState: false,
      isFormTags: false,
    } as IState;
  },
  methods: {
    apiGet() {
      api.get(
        {
          state: this.state,
        },
        (res) => {
          this.extensions = res.extensions;
          if (this.state === 'All') {
            this.allTagNames = res.allTagNames;
            this.allCount = res.allCount;
            this.pendingCount = res.pendingCount;
            this.publishedCount = res.publishedCount;
            this.rejectedCount = res.rejectedCount;
          }
        },
      );
    },

    apiGetReleases(extension: IExtension) {
      api.getReleases(
        {
          userName: extension.userName,
          name: extension.name,
        },
        (res) => {
          this.releases = res.releases;
          this.isTableView = true;
          this.extension = extension;
        },
      );
    },

    apiDelete(extension: IExtension) {
      api.delete(
        {
          userName: extension.userName,
          name: extension.name,
        },
        (res) => {
          if (!res) return;
          this.state = 'All';
          utils.success('插件删除成功！');
          this.apiGet();
        },
      );
    },

    getResourceState(extension: IExtension): string {
      return utils.getResourceState(extension.state, extension.isFeatured);
    },

    getUrl(relatedUrl: string) {
      return `https://sscms.com/extensions/${relatedUrl}`;
    },

    getDownloadUrl(extension: IExtension) {
      return `https://dl.sscms.com/extensions/${extension.userName}/${extension.name}/${extension.userName}.${extension.name}.${extension.latestVersion}.zip`;
    },

    getIconUrl(extension: IExtension) {
      if (extension.iconUrl) {
        return `https://storage.sscms.com/${utils.trim(extension.iconUrl, '/')}`;
      }
      return '';
    },

    btnViewClick(extension: IExtension) {
      this.apiGetReleases(extension);
    },

    btnNavClick() {
      this.apiGet();
    },

    btnDeleteClick(extension: IExtension) {
      utils.alertDelete(
        {
          title: '删除插件',
          text: `此操作将删除插件 ${extension.userName}.${extension.name}，确定吗？`,
          button: '确认删除',
        },
        () => {
          this.apiDelete(extension);
        },
      );
    },

    btnStateClick(extension: IExtension) {
      this.extension = extension;
      this.isFormState = true;
    },

    btnTagsClick(extension: IExtension) {
      this.extension = extension;
      this.isFormTags = true;
    },

    updateExtension(value: IExtension) {
      const index = this.extensions.findIndex((x) => x.id === value.id);
      if (index !== -1) {
        this.extensions[index] = value;
      }
      this.isFormState = false;
      this.isFormTags = false;
    },
  },
  mounted() {
    this.apiGet();
  },
});
</script>
